import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import AuthImage from "../components/AuthImage";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Image as ImageIcon, MessageCircle } from "lucide-react";

export default function Albums() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isNsfw, setIsNsfw] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/albums${user && user !== false ? `?owner=${encodeURIComponent(user.username)}&pool=private` : ""}`).then((r) => setAlbums(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const create = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      // Profil-separasjon (Iter 47): album opprettet her havner i PRIVAT-poolen
      await api.post("/albums", { title, description, is_nsfw: isNsfw, owner_pool: "private" });
      setTitle(""); setDescription(""); setShowNew(false);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (!user || user === false) {
    return (
      <Layout>
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] px-5 py-10 text-center">
          <ImageIcon className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-300 mb-4">{t("album.login_required")}</p>
          <Button asChild className="btn-purple"><Link to="/login">{t("auth.log_in_btn")}</Link></Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-white">{t("album.my_albums")}</h1>
          <p className="text-zinc-400 text-sm mt-1">{t("album.private_pool_hint")}</p>
        </div>
        <Button onClick={() => setShowNew((s) => !s)} className="btn-purple" data-testid="new-album-button">
          <Plus className="h-4 w-4 mr-1.5" /> {t("album.create_new")}
        </Button>
      </div>

      {user?.is_creator && (
        <div className="mb-6 rounded-lg border border-[var(--magenta)]/30 bg-[var(--magenta)]/5 px-4 py-3 text-sm text-[var(--cream-soft)]" data-testid="creator-pool-hint">
          <span className="text-[var(--magenta)] font-meta tracking-[0.15em] uppercase">{t("album.creator_tip")}</span> {t("album.creator_pool_explain_pre")} <Link to="/min-skaperside" className="underline text-[var(--magenta)]">{t("album.creator_pool_link")}</Link> {t("album.creator_pool_explain_post")}
        </div>
      )}

      {showNew && (
        <form onSubmit={create} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5 space-y-3 mb-6" data-testid="new-album-form">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("album.album_name")}
                 className="input-dark w-full h-10 px-3 rounded-md text-sm" required minLength={2}
                 data-testid="new-album-title" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                    placeholder={t("album.album_description")}
                    className="input-dark w-full p-3 rounded-md text-sm"
                    data-testid="new-album-description" />
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <Checkbox checked={isNsfw} onCheckedChange={(v) => setIsNsfw(!!v)} data-testid="new-album-nsfw" />
            <span>{t("album.marked_nsfw")}</span>
          </label>
          {error && <div className="text-sm text-rose-400">{error}</div>}
          <Button type="submit" disabled={loading} className="btn-purple" data-testid="new-album-submit">
            {loading ? t("album.creating") : t("album.create_album")}
          </Button>
        </form>
      )}

      {albums.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] px-5 py-10 text-center text-zinc-500 text-sm">
          {t("album.no_albums")}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {albums.map((a) => (
            <Link key={a.id} to={`/albums/${a.id}`} className="group rounded-lg border border-white/10 bg-[#0a0a0a] overflow-hidden">
              <div className="aspect-square bg-zinc-900 overflow-hidden">
                {a.cover_image_id ? (
                  <AuthImage imageId={a.cover_image_id} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-700">
                    <ImageIcon className="h-10 w-10" />
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-sm text-white truncate">{a.system_kind ? "★ " : ""}{a.title}</div>
                <div className="text-xs text-zinc-500 flex justify-between items-center">
                  <span className="flex items-center gap-3">
                    <span>{t("album.photos_count_other", { count: a.image_count })}</span>
                    {a.comment_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-[var(--cream-soft)]" data-testid={`album-comment-count-${a.id}`}>
                        <MessageCircle className="h-3 w-3 text-[var(--magenta)]" />
                        {a.comment_count}
                      </span>
                    )}
                  </span>
                  <span>{formatRelative(a.created_at)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
